import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, Table } from "react-bootstrap";
import DoughnutChart from "../DoughnutChart";

const MonthlyCostRow = ({ details, monthlyCostDetailData, doughnutData, isMonthlyCostMissing, isDriftKostnadMissing, propertyType, onOpenSettings }) => {

    const formatCurrency = (value) => {
      if (isNaN(value)) return value;
      return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
        .format(value)
        .replace("kr", "")
        .trim();
    };

    // Separate loan costs from operating costs
    const loanCosts = monthlyCostDetailData.filter(item =>
      item.name === "Ränta" || item.name === "Amortering" || item.name === "Fastighetsskatt"
    );
    const operatingCosts = monthlyCostDetailData.filter(item =>
      item.name === "Driftkostnad" || item.name === "Avgift" || item.name === "Månadsavgift"
    );
    
    // Calculate totals
    const loanTotal = loanCosts.reduce((sum, item) => {
      const value = parseFloat(item.value.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    
    const operatingTotal = operatingCosts.reduce((sum, item) => {
      const value = parseFloat(item.value.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    
    const grandTotal = loanTotal + operatingTotal;

    return (
        <Card>
            <Card.Body>
            <Card.Title>Månadskostnad för {propertyType === "villa" ? "Villa/Äganderätt" : "Bostadsrätt"} - Fördelning</Card.Title>
            {(isMonthlyCostMissing || isDriftKostnadMissing) && (
                <div style={{
                    backgroundColor: '#FFF3CD',
                    padding: '1rem',
                    borderRadius: '6px',
                    borderLeft: '4px solid #FFC107',
                    marginBottom: '1rem'
                }}>
                    <p style={{
                        fontSize: '0.85rem',
                        color: '#664D03',
                        margin: 0,
                        lineHeight: '1.5',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                    }}>
                        ⚠️ Beräkningen är inte korrekt
                    </p>
                    <p style={{
                        fontSize: '0.85rem',
                        color: '#664D03',
                        margin: 0,
                        lineHeight: '1.5'
                    }}>
                        {propertyType === "villa" ? (
                            <>Driftkostnaden saknas</>
                        ) : (
                            <>
                                {isMonthlyCostMissing && isDriftKostnadMissing && "Månadsavgiften och driftkostnaden saknas"}
                                {isMonthlyCostMissing && !isDriftKostnadMissing && "Månadsavgiften saknas"}
                                {!isMonthlyCostMissing && isDriftKostnadMissing && "Driftkostnaden saknas"}
                            </>
                        )} vilket gör att månadskostnaderna inte kan beräknas korrekt. {' '}
                        <button
                            onClick={() => {
                                if (propertyType === "villa") {
                                    onOpenSettings('driftkostnad');
                                } else if (isMonthlyCostMissing && isDriftKostnadMissing) {
                                    onOpenSettings('avgift');
                                } else if (isMonthlyCostMissing) {
                                    onOpenSettings('avgift');
                                } else {
                                    onOpenSettings('driftkostnad');
                                }
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#664D03',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit'
                            }}
                        >
                            Lägg till värden här
                        </button>
                    </p>
                </div>
            )}
            <p>Detta blir din totala månadskostnad för {details.propertyType === "villa" ? "villa/äganderätt" : "bostadsrätt"} vid en köpeskilling på <b>{formatCurrency(details.utgangspris)} kr</b>. Beräkningen inkluderar bolåneränta, amortering enligt svenska bankregler, fastighetsskatt (max {details.propertyType === "villa" ? "10,425 kr/år" : "1,564 kr/år"}) och {details.propertyType === "villa" ? "driftkostnader" : "månadsavgift till BRF"}.</p>
            <Table striped bordered hover>
                <thead>
                <tr>
                    <th width="35%"></th>
                    <th>Summa <i><small>per månad</small></i></th>
                </tr>
                </thead>
                <tbody>
                {/* Loan costs */}
                {loanCosts.map((item, index) => (
                    <tr key={`loan-${index}`}>
                    <td>{item.name}</td>
                    <td><b>{item.value} kr</b></td>
                    </tr>
                ))}
                {/* Loan subtotal */}
                <tr style={{backgroundColor: '#f8f9fa', fontWeight: 'bold'}}>
                    <td><strong>Totala lånekostnader</strong></td>
                    <td><strong>{formatCurrency(loanTotal)} kr</strong></td>
                </tr>
            </tbody>
            </Table>
            <Table striped bordered hover>
                <thead>
                <tr>
                    <th width="35%"></th>
                    <th>Summa <i><small>per månad</small></i></th>
                </tr>
                </thead>
                <tbody>
                {/* Operating costs */}
                {operatingCosts.map((item, index) => (
                    <tr key={`operating-${index}`}>
                    <td>{item.name}</td>
                    <td><b>{item.value} kr</b></td>
                    </tr>
                ))}
                {/* Grand total */}
                <tr style={{backgroundColor: '#e9ecef', fontWeight: 'bold'}}>
                    <td><strong>Totala månadskostnaden</strong></td>
                    <td><strong>{formatCurrency(grandTotal)} kr</strong></td>
                </tr>
                </tbody>
            </Table>
            </Card.Body>
        </Card>
    );
}

// Requirements and export

const detailsPropTypes = PropTypes.shape({
    utgangspris: PropTypes.number.isRequired,
    driftkostnad: PropTypes.number.isRequired,
    taxeringsvarde: PropTypes.number.isRequired,
    pantbrev: PropTypes.number.isRequired,
    nyaPantbrev: PropTypes.number.isRequired,
    kostnadNyaPantbrev: PropTypes.number.isRequired,
    kostnadLagfart: PropTypes.number.isRequired,
});

const monthlyCostDetailDataPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })
);

const doughnutDataPropTypes = PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired, 
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  );

MonthlyCostRow.propTypes = {
    details: detailsPropTypes,
    monthlyCostDetailData: monthlyCostDetailDataPropTypes,
    doughnutData: doughnutDataPropTypes,
    isMonthlyCostMissing: PropTypes.bool,
    isDriftKostnadMissing: PropTypes.bool,
    propertyType: PropTypes.string,
    onOpenSettings: PropTypes.func
};

export default MonthlyCostRow;